import * as THREE from "three";
import { PATH_SCENERY, type Scenery, type SceneryType } from "../../prims/scenery";
import { mergeStatic } from "../merge";
import { carousel } from "./carousel";
import { castle } from "./castle";
import { coaster } from "./coaster";
import { ferrisWheel } from "./ferris-wheel";
import { flume } from "./flume";
import { balloons, bench, building, fence, lamp, lights, paving, tent, tree, umbrella } from "./furniture";
import { beam, booth, catwalk, crate, duct, factoryWindow, mural, pallet, pendant, pipes, plywood, shaft, truss } from "./indoor";
import { Kit } from "./kit";
import { burgerStand, foodStand, stall } from "./stalls";
import { waterslide } from "./waterslide";

export { tickers, type Tick } from "./kit";

// The scenery registry (#156): each scenery type's mesh, one file per ride or
// building. A type missing here doesn't compile. Builders draw in the
// piece's local frame (x along u, z along w) unless the type follows a path,
// whose points are already in world space.

type SceneryBuilder = (kit: Kit, s: Scenery) => THREE.Object3D;

/** The types that move (and so aren't batched with the rest): built with their own geometries. */
const MOVING: readonly SceneryType[] = ["ferris-wheel", "carousel"];

const SCENERY_MESHES: { readonly [K in SceneryType]: SceneryBuilder } = {
  "ferris-wheel": ferrisWheel,
  carousel,
  coaster,
  flume,
  slide: waterslide,
  stall,
  food: foodStand,
  burger: burgerStand,
  castle,
  tent,
  lamp,
  lights,
  fence,
  bench,
  umbrella,
  balloons,
  tree,
  paving,
  building,
  truss,
  beam,
  catwalk,
  window: factoryWindow,
  pendant,
  pipes,
  duct,
  mural,
  pallet,
  crate,
  booth,
  plywood,
  shaft,
};

/** One scenery piece's mesh, built by `kit` and placed where the piece stands (a path's points are already in place). */
function placed(kit: Kit, s: Scenery): THREE.Object3D {
  const obj = SCENERY_MESHES[s.type](kit, s);
  if (!PATH_SCENERY.includes(s.type)) {
    obj.position.set(s.x, 0, s.z);
    obj.rotation.y = -s.rot; // local +x along the piece's u axis
    obj.scale.setScalar(s.size);
  }
  return obj;
}

/**
 * A map's scenery: everything that stands still batched into as few meshes
 * as its materials allow, and the moving rides (the wheel, the carousel)
 * kept apart with their `tick` (see `tickers`).
 */
export function buildScenery(list: readonly Scenery[]): THREE.Group {
  const kit = new Kit();
  const own = new Kit(false);
  const root = new THREE.Group();
  const still = new THREE.Group();
  for (const s of list) {
    const moving = MOVING.includes(s.type);
    (moving ? root : still).add(placed(moving ? own : kit, s));
  }
  if (still.children.length) root.add(mergeStatic(still));
  return root;
}

/**
 * Still scenery built into `parent` and left for the caller to batch with
 * its own parts (#216: Bomb the Hill's roadside, merged into its chunk's
 * batch), every build taking its materials from `mats` so they're shared
 * and the caller frees them. No moving types.
 */
export function addStillScenery(list: readonly Scenery[], parent: THREE.Object3D, mats: Map<string, THREE.Material>): void {
  const kit = new Kit(true, mats);
  for (const s of list) {
    if (MOVING.includes(s.type)) throw new Error(`addStillScenery: ${s.type} moves`);
    parent.add(placed(kit, s));
  }
}
